import { format, type Locale } from "date-fns";
import { enUS } from "date-fns/locale";
import { es } from "date-fns/locale";

const FALLBACK = "—";

/**
 * Parse a date-only string (YYYY-MM-DD or ISO with time) as local midnight.
 * Use for fiscal due dates and any date-only value so comparisons are correct
 * in all timezones (new Date("2026-02-24") is UTC midnight and shifts to
 * the previous day in timezones behind UTC when using setHours(0,0,0,0)).
 */
export function parseLocalDateOnly(isoDate: string): Date {
  const s = isoDate.slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
    return new Date(isoDate);
  }
  return new Date(y, m - 1, d);
}

const DATE_ONLY_FORMAT = "d MMM yyyy";

const localeMap: Record<string, Locale> = {
  es,
  en: enUS,
};

export function getDateFnsLocale(locale?: string): Locale {
  if (!locale) return enUS;
  const normalized = locale.split("-")[0].toLowerCase();
  return localeMap[normalized] ?? enUS;
}

/**
 * Lowercase the month segment in a formatted date string (e.g. "18 Feb 2026" → "18 feb 2026").
 */
function lowercaseMonth(formatted: string): string {
  const parts = formatted.split(" ");
  if (parts.length >= 2) {
    parts[1] = parts[1].toLowerCase();
    return parts.join(" ");
  }
  return formatted;
}

export interface FormatDisplayDateOptions {
  includeTime?: boolean;
  locale?: string;
  /**
   * When true and includeTime is false, format using the UTC calendar date
   * (year, month, day) so that date-only values like 2026-02-18T00:00:00Z
   * display as "18 feb 2026" in any timezone instead of shifting to the
   * previous/next local day. Use for fiscal due dates and other date-only fields.
   */
  useCalendarDate?: boolean;
}

/** Uruguay is the only timezone Fiscalo cares about for user-facing time. */
const APP_TIME_ZONE = "America/Montevideo";

/**
 * Today's date (YYYY-MM-DD) in Uruguay, not the browser's local timezone or
 * UTC. `new Date().toISOString()` rolls to the next UTC calendar day at
 * 21:00 Uruguay time — using it as "today" silently pre-fills tomorrow's
 * date for anyone in the browser during that window.
 */
export function todayInUruguay(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: APP_TIME_ZONE }).format(
    new Date()
  );
}

/**
 * Tomorrow's date (YYYY-MM-DD) in Uruguay — the latest issueDate DGI
 * actually tolerates for a CFE (confirmed empirically 2026-08-28: an
 * invoice dated one day ahead was DGI-confirmed; dated 3 days ahead was
 * rejected). Use as the upper bound on an issueDate picker/validation,
 * not todayInUruguay() alone.
 */
export function tomorrowInUruguay(): string {
  const parts = todayInUruguay().split("-").map(Number);
  const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], 12));
  d.setUTCDate(d.getUTCDate() + 1);
  return new Intl.DateTimeFormat("en-CA", { timeZone: "UTC" }).format(d);
}

/**
 * Split a UTC instant (e.g. a `scheduledSendAt` from the backend) into its
 * Uruguay-local date (YYYY-MM-DD) and time (HH:mm) — for pre-filling a
 * schedule/reschedule form. Slicing the raw ISO string directly would show
 * the WRONG wall-clock time (UTC, not Uruguay's UTC-3).
 */
export function toUruguayDateTimeParts(isoInstant: string): {
  date: string;
  time: string;
} {
  const d = new Date(isoInstant);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type: string): string =>
    parts.find((p) => p.type === type)?.value ?? "";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour")}:${get("minute")}`,
  };
}

/**
 * Parse a timestamp string for the includeTime path. The backend sometimes
 * returns timezone-naive ISO strings (Postgres `timestamp without time zone`
 * columns serialize to "2026-05-22T15:55:00" without a Z), which JS would
 * interpret as the *user's* local time and produce wrong displays for
 * anyone outside server-TZ. Treat naive strings as UTC so subsequent UY
 * formatting lands on the right instant.
 */
function parseUtcTimestamp(input: string): Date {
  const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(input);
  return new Date(hasTz ? input : `${input}Z`);
}

const MONTHS_ES = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

const MONTHS_EN = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Format `d` as "22 may 2026, 12:55" in Uruguay local time. We don't rely
 * on Intl alone here because its part order varies by locale and we want
 * the same "DD month YYYY, HH:mm" shape across en/es callers.
 */
function formatInUruguayTime(d: Date, locale: string | undefined): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes): string => {
    const p = parts.find((x) => x.type === type);
    return p?.value ?? "";
  };
  const day = Number(get("day"));
  const month = Number(get("month")) - 1;
  const year = get("year");
  const hour = get("hour");
  const minute = get("minute");
  const months = locale?.toLowerCase().startsWith("en") ? MONTHS_EN : MONTHS_ES;
  const monthLabel = months[month] ?? "";
  return `${day} ${monthLabel} ${year}, ${hour}:${minute}`;
}

/**
 * Formats a date for user-facing display: "18 feb 2026" or "18 feb 2026, 16:08".
 * Invalid dates return "—".
 *
 * Parsing strategy depends on `includeTime`:
 *  - `includeTime: true` — parse the input as UTC (assume Z when missing)
 *    and format in America/Montevideo so the user always sees Uruguay
 *    local time regardless of where their browser is. parseLocalDateOnly
 *    truncates to YYYY-MM-DD and produces local midnight, which was
 *    rendering every timestamp as "00:00" in UTC.
 *  - `includeTime: false` (default) — keep parseLocalDateOnly so date-only
 *    values like "2026-02-24" don't shift across timezone boundaries.
 */
export function formatDisplayDate(
  date: string | Date | null | undefined,
  options?: FormatDisplayDateOptions
): string {
  // 134+ call sites; guard runtime nullish even though the TS signature is
  // string | Date. A caller passing null/undefined (e.g. an optional API
  // field that turned out unset) would otherwise throw on `d.getTime()` and
  // crash whichever component is rendering — easier to render "—".
  if (date == null) return FALLBACK;

  if (options?.includeTime) {
    const d = typeof date === "string" ? parseUtcTimestamp(date) : date;
    if (Number.isNaN(d.getTime())) return FALLBACK;
    return formatInUruguayTime(d, options.locale);
  }

  const d = typeof date === "string" ? parseLocalDateOnly(date) : date;
  if (Number.isNaN(d.getTime())) return FALLBACK;

  const locale = getDateFnsLocale(options?.locale);
  let toFormat = d;
  if (options?.useCalendarDate) {
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    const day = d.getUTCDate();
    toFormat = new Date(y, m, day);
  }
  const formatted = format(toFormat, DATE_ONLY_FORMAT, { locale });
  return lowercaseMonth(formatted);
}
